import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';

// Same operations waybetter-mobile's CreditCardsScreen/AddCreditCardScreen
// and its own checkout screen's paymentInfoQuery use. Despite the name,
// wb-billing-service's `creditCards` isn't a separate local record of its
// own - User#credit_cards (wb-billing-service) is a live
// braintree_customer.credit_cards call, so this is Braintree's own vault,
// just read server-side through our own GraphQL layer instead of
// braintree-web's client-side vault-manager (see each Hosted Fields
// caller's own comment for why that one wasn't used directly). Shared by
// every Hosted Fields caller that offers saved cards
// (Supercharger/PaymentHostedFields, Cart/PaymentHostedFields) - nothing
// caller-specific about the operations themselves, just about where
// they're wired up.
const CREDIT_CARDS_QUERY = gql`
  query UserCreditCards($userId: Int!) {
    user(id: $userId) {
      id
      defaultPaymentMethod {
        token
        type
      }
      creditCards {
        id
        token
        cardType
        last4
        expirationMonth
        expirationYear
        default
      }
    }
  }
`;

const CREATE_CREDIT_CARD_MUTATION = gql`
  mutation CreateCreditCard($userId: ID!, $paymentMethodNonce: String!) {
    createCreditCard(userId: $userId, paymentMethodNonce: $paymentMethodNonce) {
      id
    }
  }
`;

const REMOVE_CREDIT_CARD_MUTATION = gql`
  mutation RemoveCreditCard($userId: ID!, $token: String!) {
    removeCreditCard(userId: $userId, token: $token) {
      id
    }
  }
`;

// The card to preselect: cross-referenced against defaultPaymentMethod's
// own token first - the same way waybetter-mobile's own
// membership-cancellation-context derives its payment method display,
// and more authoritative than a card's own `default` flag in isolation
// (that flag is set once, at creation time via createCreditCard's
// makeDefault, and can drift from whichever token actually gets charged
// for "pay with card on file" - see wb-billing-service's
// DefaultPaymentMethodInfo). Falls back to `default`, then just the first
// card, if defaultToken doesn't match anything in the list (e.g. the
// default is a non-card payment method).
export const preferredCard = (creditCards, defaultToken) =>
  creditCards.find((card) => card.token === defaultToken) ||
  creditCards.find((card) => card.default) ||
  creditCards[0] ||
  null;

export function useCreditCards({ userId }) {
  const { data, loading, refetch } = useQuery(CREDIT_CARDS_QUERY, {
    skip: !userId,
    variables: { userId },
    fetchPolicy: 'network-only',
  });

  return {
    creditCards: data?.user?.creditCards || [],
    defaultToken: data?.user?.defaultPaymentMethod?.token,
    loading,
    refetch,
  };
}

export function useCreateCreditCard() {
  const [mutate] = useMutation(CREATE_CREDIT_CARD_MUTATION);

  const createCreditCard = ({ userId, paymentMethodNonce }) =>
    mutate({ variables: { userId, paymentMethodNonce } });

  return { createCreditCard };
}

export function useRemoveCreditCard() {
  const [mutate] = useMutation(REMOVE_CREDIT_CARD_MUTATION);

  const removeCreditCard = ({ userId, token }) =>
    mutate({ variables: { userId, token } });

  return { removeCreditCard };
}
